//LIBRARIES
import React, { useState } from 'react';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { ButtonPrimary } from '@yuhu-inc/yuhui';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import withMobileDialog from '@material-ui/core/withMobileDialog';

//ACTIONS
import { getTenant } from 'actions/landlordTenantsActions';

//COMPONENTS
import Header from 'components/shared/Header';
import { RenderIfToggleOn } from 'components/shared/ToggleRenderer';
import NotificationPreferencesOverview from 'components/landlord_app/tenants/components/NotificationPreferencesOverview';

//ASSETS
import HornIcon from 'assets/horn_icon.png';
import ConfettiIcon from 'assets/confetti_icon.png';
import PartyIcon from 'assets/party_icon.png';

//HELPERS
import { media } from 'helpers/MediaHelper';
import { getTranslatedString } from 'helpers/i18n';
import { colors } from 'helpers/ColorHelper';
import LegalNoticesOptIn from './LegalNoticesOptIn';

const StyledContainer = styled.div``;

const StyledBody = styled.div`
  margin: 15px 15px 25px;
`;

const StyledDialogContent = styled(DialogContent)`
  ${media.mobile`
    padding-top: 30% !important;
    background-color: ${colors.DIALOG_BACKGROUND};
  `};
`;

const StyledBodyHeader = styled.div`
  font-family: Circular-Bold;
  font-size: 16px;
  margin-bottom: 10px;
`;

const StyledBodyVerbiage = styled.div`
  margin-bottom: 25px;
`;

const StyledButtonContainer = styled.div`
  text-align: center;
`;

const StyledDialogBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: ${(props) => (props.noDialogVerbiage ? '20px 15px 0px' : '20px 15px')};
`;

const StyledDialogHeader = styled.div`
  font-family: Circular-Bold;
  font-size: 20px;
  margin-bottom: 10px;
`;

const StyledDialogVerbiage = styled.div`
  text-align: center;
`;

const StyledDialogActionsContainer = styled(DialogActions)`
  justify-content: ${(props) => (props.justifyContentCenter ? 'center' : 'space-between')};
  padding: 16px 24px 16px;
  text-transform: none !important;
  background-color: #fff;
  ${media.mobile`
    margin: 0px;
    border-top: 1px solid ${colors.FOOTER_SHADOW};
    box-shadow: 0px -3px 13px rgba(0, 0, 0, 0.0830146)
  `};
`;

const OPT_OUT = 'opt_out';
const OPT_IN = 'opt_in';

const TenantOptInOptOut = ({
  fullScreen,
  optOutLead,
  optInLead,
  optInOptOut,
  tenantDetails,
  getTenant,
}) => {
  const [toggleConfirmationDialog, setToggleConfirmationDialog] = useState(false);

  let bodyHeader = getTranslatedString('landlordPortal.tenantOptInOptOut.optOutOfPortal');
  let bodyVerbiage = getTranslatedString(
    'landlordPortal.tenantOptInOptOut.ifRequestedYouCanOptOut'
  );
  let bodyAction = 'landlordPortal.tenantOptInOptOut.optOut';
  if (optInOptOut === 'opt_in') {
    bodyHeader = getTranslatedString('landlordPortal.tenantOptInOptOut.optInToPortal');
    bodyVerbiage = getTranslatedString('landlordPortal.tenantOptInOptOut.thisResidentHasRequested');
    bodyAction = 'landlordPortal.tenantOptInOptOut.optIn';
  }

  return (
    <StyledContainer>
      <Header name={getTranslatedString('landlordPortal.tenantOptInOptOut.accountUpcase')} />
      <NotificationPreferencesOverview tenantDetails={tenantDetails} />
      <RenderIfToggleOn toggle="legal_documents_as_notices">
        <LegalNoticesOptIn tenantDetails={tenantDetails} />
      </RenderIfToggleOn>
      <RenderIfToggleOn toggle="ui.residents.account.show_opt_out">
        <StyledBody>
          <StyledBodyHeader>{bodyHeader}</StyledBodyHeader>
          <StyledBodyVerbiage>{bodyVerbiage}</StyledBodyVerbiage>
          <StyledButtonContainer>
            <ButtonPrimary
              type="button"
              style={{ height: '32px', lineHeight: '28px', padding: '1px 40px' }}
              onClick={() => setToggleConfirmationDialog(true)}
              contentKey={bodyAction}
            />
          </StyledButtonContainer>
        </StyledBody>
      </RenderIfToggleOn>
      {optInOptOut === OPT_OUT && (
        <OptOutConfirmationDialog
          fullScreen={fullScreen}
          toggleConfirmationDialog={toggleConfirmationDialog}
          setToggleConfirmationDialog={setToggleConfirmationDialog}
          optOutLead={optOutLead}
          getTenant={getTenant}
          tenantDetails={tenantDetails}
        />
      )}
      {optInOptOut === OPT_IN && (
        <OptInConfirmationDialog
          fullScreen={fullScreen}
          toggleConfirmationDialog={toggleConfirmationDialog}
          setToggleConfirmationDialog={setToggleConfirmationDialog}
          optInLead={optInLead}
          getTenant={getTenant}
          tenantDetails={tenantDetails}
        />
      )}
    </StyledContainer>
  );
};

export default withMobileDialog()(
  connect(
    (store) => ({
      tenantDetails: store.landlordTenants.tenantDetails,
    }),
    {
      getTenant,
    }
  )(TenantOptInOptOut)
);

const CONFIRM_DIALOG = 'confirm_dialog';
const CONFIRMED_DIALOG = 'confirmed_dialog';

const OptOutConfirmationDialog = ({
  fullScreen,
  toggleConfirmationDialog,
  setToggleConfirmationDialog,
  optOutLead,
  getTenant,
  tenantDetails,
}) => {
  const [dialogType, setDialogType] = useState(CONFIRM_DIALOG);
  const [isConfirming, setIsConfirming] = useState(false);

  const handleOptOutLead = () => {
    optOutLead()
      .then(() => {
        setDialogType(CONFIRMED_DIALOG);
        setIsConfirming(false);
      })
      .catch((err) => {
        setIsConfirming(false);
        console.error('request failed', err);
      });
  };

  const handlePrimaryAction = () => {
    if (dialogType === CONFIRM_DIALOG) {
      setIsConfirming(true);
      handleOptOutLead();
      return;
    }
    setToggleConfirmationDialog(false);
    getTenant(tenantDetails.id, { all_maintenance_tickets: true });
  };

  let icon = HornIcon;
  let dialogHeaderVerbiage = getTranslatedString('landlordPortal.tenantOptInOptOut.optOutResident');
  let dialogVerbiage = getTranslatedString('landlordPortal.tenantOptInOptOut.whileOptedOut');

  if (dialogType === CONFIRMED_DIALOG) {
    icon = ConfettiIcon;
    dialogHeaderVerbiage = getTranslatedString('landlordPortal.tenantOptInOptOut.residentOptedOut');
    dialogVerbiage = '';
  }

  return (
    <Dialog
      disableBackdropClick
      fullScreen={fullScreen}
      open={toggleConfirmationDialog}
      classes={{
        paper: 'form-dialog',
      }}
      style={{ zIndex: 3000 }}
    >
      <StyledDialogContent>
        <StyledDialogBody noDialogVerbiage={dialogType === CONFIRMED_DIALOG}>
          <img src={icon} style={{ marginBottom: '15px' }} alt="" />
          <StyledDialogHeader>{dialogHeaderVerbiage}</StyledDialogHeader>
          <StyledDialogVerbiage>{dialogVerbiage}</StyledDialogVerbiage>
        </StyledDialogBody>
      </StyledDialogContent>
      <StyledDialogActionsContainer justifyContentCenter={dialogType === CONFIRMED_DIALOG}>
        {dialogType === CONFIRM_DIALOG && (
          <ButtonPrimary
            type="button"
            style={{
              height: '32px',
              lineHeight: '28px',
              backgroundColor: 'white',
            }}
            onClick={() => setToggleConfirmationDialog(false)}
            contentKey="landlordPortal.tenantOptInOptOut.cancel"
          />
        )}

        <ButtonPrimary
          type="button"
          style={{
            height: '32px',
            lineHeight: '28px',
            padding: '1px 40px',
          }}
          disabled={isConfirming}
          onClick={() => handlePrimaryAction()}
          contentKey={
            dialogType === CONFIRM_DIALOG
              ? 'landlordPortal.tenantOptInOptOut.confirm'
              : 'landlordPortal.tenantOptInOptOut.close'
          }
        />
      </StyledDialogActionsContainer>
    </Dialog>
  );
};

const OptInConfirmationDialog = ({
  fullScreen,
  toggleConfirmationDialog,
  setToggleConfirmationDialog,
  optInLead,
  getTenant,
  tenantDetails,
}) => {
  const [dialogType, setDialogType] = useState(CONFIRM_DIALOG);
  const [isConfirming, setIsConfirming] = useState(false);

  const handleOptInLead = () => {
    optInLead()
      .then(() => {
        setDialogType(CONFIRMED_DIALOG);
        setIsConfirming(false);
      })
      .catch((err) => {
        setIsConfirming(false);
        console.error('request failed', err);
      });
  };

  const handlePrimaryAction = () => {
    if (dialogType === CONFIRM_DIALOG) {
      setIsConfirming(true);
      handleOptInLead();
      return;
    }
    setToggleConfirmationDialog(false);
    getTenant(tenantDetails.id, { all_maintenance_tickets: true });
  };

  let icon = PartyIcon;
  let dialogHeaderVerbiage = getTranslatedString(
    'landlordPortal.tenantOptInOptOut.optInToPortalQuestion'
  );
  let dialogVerbiage = getTranslatedString('landlordPortal.tenantOptInOptOut.onceOptedIn');

  if (dialogType === CONFIRMED_DIALOG) {
    icon = ConfettiIcon;
    dialogHeaderVerbiage = getTranslatedString('landlordPortal.tenantOptInOptOut.residentOptedIn');
    dialogVerbiage = getTranslatedString('landlordPortal.tenantOptInOptOut.youCanNowInvite');
  }

  return (
    <Dialog
      disableBackdropClick
      fullScreen={fullScreen}
      open={toggleConfirmationDialog}
      classes={{
        paper: 'form-dialog',
      }}
      style={{ zIndex: 3000 }}
    >
      <StyledDialogContent>
        <StyledDialogBody>
          <img src={icon} style={{ marginBottom: '15px' }} alt="" />
          <StyledDialogHeader>{dialogHeaderVerbiage}</StyledDialogHeader>
          <StyledDialogVerbiage>{dialogVerbiage}</StyledDialogVerbiage>
        </StyledDialogBody>
      </StyledDialogContent>
      <StyledDialogActionsContainer justifyContentCenter={dialogType === CONFIRMED_DIALOG}>
        {dialogType === CONFIRM_DIALOG && (
          <ButtonPrimary
            type="button"
            style={{
              height: '32px',
              lineHeight: '28px',
              backgroundColor: 'white',
            }}
            onClick={() => setToggleConfirmationDialog(false)}
            contentKey="landlordPortal.tenantOptInOptOut.cancel"
          />
        )}
        <ButtonPrimary
          type="button"
          style={{
            height: '32px',
            lineHeight: '28px',
            padding: '1px 40px',
          }}
          disabled={isConfirming}
          onClick={() => handlePrimaryAction()}
          contentKey={
            dialogType === CONFIRM_DIALOG
              ? 'landlordPortal.tenantOptInOptOut.confirm'
              : 'landlordPortal.tenantOptInOptOut.close'
          }
        />
      </StyledDialogActionsContainer>
    </Dialog>
  );
};
